import React, { useEffect, useState } from 'react';
import { Box, Divider, Icon, IconButton, Tooltip, Typography } from '@mui/material';
import FilterCategoryMultiSelect from '../controls/FilterCategoryMultiSelect';
import MaStatusSwitch, { MaStatusSwitchOption } from '../controls/MaStatusSwitch';
import { portfolioService } from '../../services/portfolio.service';
import { PortfolioFilterOption } from '../../models/portfolio/portfolioFilters';

interface ThreeStepStatusFilterProps {
  label: string;
  value: string[];
  onChange: (values: string[]) => void;
  options: MaStatusSwitchOption[];
}

const ThreeStepStatusFilter: React.FC<ThreeStepStatusFilterProps> = ({ label, value, onChange, options }) => {
  const selectedValue = value.length === 1 ? value[0] : 'all';

  return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="body2" sx={{ textAlign: 'left', minWidth: 0 }}>{label}</Typography>
        <MaStatusSwitch
          options={options}
          selectedValue={selectedValue}
          onSelectedValueChange={(nextValue) => onChange(nextValue === 'all' ? [] : [nextValue])}
        />
      </Box>
  );
};

const AFSTEMMING_FILTER_OPTIONS: MaStatusSwitchOption[] = [
  { value: 'all', text: 'Alles', thumbColor: '#ffffff', iconColor: '#616161', tooltip: 'Alles tonen' },
  { value: 'in_afstemming', icon: 'handshake', thumbColor: '#fff0d9', iconColor: '#ff9800', tooltip: 'In afstemming' },
  { value: 'afgestemd', icon: 'handshake', thumbColor: '#e4f3e5', iconColor: '#4caf50', tooltip: 'Afgestemd' },
];

const DEELNAME_FILTER_OPTIONS: MaStatusSwitchOption[] = [
  { value: 'all', text: 'Alles', thumbColor: '#ffffff', iconColor: '#616161', tooltip: 'Alles tonen' },
  { value: 'pending', icon: 'pause', thumbColor: '#fff0d9', iconColor: '#ff9800', tooltip: 'Wacht op reactie' },
  { value: 'accepted', icon: 'check', thumbColor: '#e4f3e5', iconColor: '#4caf50', tooltip: 'Actief' },
];

const sortOptionsByName = (options: PortfolioFilterOption[]): PortfolioFilterOption[] =>
  [...options]
    .filter((option) => option?.id)
    .map((option) => ({ id: option.id, name: option.name || option.id }))
    .sort((first, second) => first.name.localeCompare(second.name, 'nl-NL'));

interface FilterPortfoliosProps {
  statusFilter: string[];
  myPlansFilter: string[];
  myOrganisatieFilter: string[];
  organizationFilter: string[];
  consortiumFilter: string[];
  onChangeStatus: (values: string[]) => void;
  onChangeMyPlans: (values: string[]) => void;
  onChangeMyOrganisatie: (values: string[]) => void;
  onChangeOrganization: (values: string[]) => void;
  onChangeConsortium: (values: string[]) => void;
  onClearAll: () => void;
  onClose: () => void;
}

const FilterPortfolios: React.FC<FilterPortfoliosProps> = ({
  statusFilter,
  myPlansFilter,
  myOrganisatieFilter,
  organizationFilter,
  consortiumFilter,
  onChangeStatus,
  onChangeMyPlans,
  onChangeMyOrganisatie,
  onChangeOrganization,
  onChangeConsortium,
  onClearAll,
  onClose,
}) => {
  const [organizations, setOrganizations] = useState<PortfolioFilterOption[]>([]);
  const [consortiums, setConsortiums] = useState<PortfolioFilterOption[]>([]);

  const hasActiveFilters =
    statusFilter.length > 0 ||
    myPlansFilter.length > 0 ||
    myOrganisatieFilter.length > 0 ||
    organizationFilter.length > 0 ||
    consortiumFilter.length > 0;

  useEffect(() => {
    let cancelled = false;

    const fetchFilterOptions = async () => {
      try {
        const data = await portfolioService.getPortfolioFilters();
        const organizationOptions = sortOptionsByName(data.organizations);
        const consortiumOptions = sortOptionsByName(data.consortiums);

        if (!cancelled) {
          setOrganizations(organizationOptions);
          setConsortiums(consortiumOptions);
        }
      }
      catch (error) {
        console.error('Error fetching filteropties voor afstemmingsdossiers:', error);

        if (!cancelled) {
          setOrganizations([]);
          setConsortiums([]);
        }
      }
    };

    fetchFilterOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="filter-side-panel">
      <div className="filter-side-panel-header">
        <span>Uitgebreid zoeken</span>
        <div className="filter-side-panel-header-actions">
          {hasActiveFilters && (
            <Tooltip title="Verwijder alle filters">
              <IconButton size="small" onClick={onClearAll}>
                <Icon>delete</Icon>
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Sluit filter">
            <IconButton size="small" onClick={onClose}>
              <Icon>close</Icon>
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <div className="filter-side-panel-body">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '22px', mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="body2">Deelname mijn organisatie</Typography>
            <MaStatusSwitch
              options={DEELNAME_FILTER_OPTIONS}
              selectedValue={myOrganisatieFilter.length === 1 ? myOrganisatieFilter[0] : 'all'}
              onSelectedValueChange={(nextValue) => onChangeMyOrganisatie(nextValue === 'all' ? [] : [nextValue])}
            />
          </Box>

          <ThreeStepStatusFilter
            label="Deelname mijn plannen"
            value={myPlansFilter}
            onChange={onChangeMyPlans}
            options={DEELNAME_FILTER_OPTIONS}
          />

          <ThreeStepStatusFilter
            label="Afstemming"
            value={statusFilter}
            onChange={onChangeStatus}
            options={AFSTEMMING_FILTER_OPTIONS}
          />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5, textAlign: 'left' }}>
          Overige filters
        </Typography>

        <div className="filter-section">
          <FilterCategoryMultiSelect
            label="Organisaties"
            placeholder="Zoek en selecteer één of meerdere organisaties"
            noOptionsText="Geen organisaties beschikbaar"
            items={organizations}
            selectedValues={organizationFilter}
            onChange={onChangeOrganization}
            chipStyleVariant="default"
          />
        </div>

        <div className="filter-section">
          <FilterCategoryMultiSelect
            label="Samenwerkingsverbanden"
            placeholder="Zoek en selecteer één of meerdere samenwerkingsverbanden"
            noOptionsText="Geen samenwerkingsverbanden beschikbaar"
            items={consortiums}
            selectedValues={consortiumFilter}
            onChange={onChangeConsortium}
            chipStyleVariant="default"
          />
        </div>
      </div>
    </div>
  );
};

export default FilterPortfolios;
